import './BrowserColumn.scss';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import RequestsBrowser from '../RequestsBrowser';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LabelSwitch from '../common/LabelSwitch';
import { useQueueContext } from '../QueueContext';
import { IconAdd } from 'components/Icons';
import { QueueCreateAskReq } from '../../AskRequest';
import withQueueBrowser from './withQueueBrowser';
import { QueueEventPayload } from '../../../../hooks/useEvent';
import { useAuthContext } from 'context/useAuthContext';
import { AskRequestStatus, HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';
import { SwitchProps } from '../common/LabelSwitch/LabelSwitch';
import { QueueLabelMap } from '../utils/askRequestUtils';
import { useQueryState } from 'hooks/useQueryState';

const EnhancedLabelSwitch = withQueueBrowser(LabelSwitch);

const BrowserColumn = () => {
  const prefix = prefixClass('browser-column');
  const { currentLocationAbility } = useAuthContext();
  const { userId } = useAuthContext();
  const [createRequestModalOpen, setCreateRequestModalOpen] = useState<boolean>(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [requestId] = useQueryState('requestId');

  const [filterMentions] = useQueryState('filterMentions');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    state: { filters, selectedItem, requests },
    dispatch,
  } = useQueueContext();

  const toggleCreateRequestModal = () => {
    setCreateRequestModalOpen(val => !val);
  };

  const requestsByStatus = useMemo(() => {
    return requests
      .filter(req => req.status === filters.status)
      .sort((a, b) => {
        return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
      })
      .reverse();
  }, [requests, filters.status]);

  useEffect(() => {
    if (requestId) {
      //select request by id
      const request = requests.find(req => req.id === requestId);
      if (request) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
      }
    } else {
      const firstMatchingRequest = requestsByStatus[requestsByStatus.length - 1];
      if (firstMatchingRequest) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: firstMatchingRequest });
        const newParams = new URLSearchParams(searchParams);
        newParams.set('requestId', firstMatchingRequest.id);
        newParams.set('status', firstMatchingRequest.status);
        navigate(`?${newParams.toString()}`, { replace: true });
      }
    }
  }, []);

  const handleStatusChange = (status: AskRequestStatus) => {
    // Update the filters first
    dispatch({ type: 'SET_FILTERS', payload: { ...filters, status, sort: status !== 'Closed' ? 'oldest' : 'newest' } });
    //validate if mentions enabled
    //sort request by date
    const requestsByDate = requests.sort((a, b) => {
      return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
    });

    let firstMatchingRequest = [];
    if (filterMentions === 'true' && userId) {
      firstMatchingRequest = requestsByDate.filter(
        req => req.status === status && req.comments?.some(comment => comment.taggedUserIds?.includes(userId!))
      );
    } else {
      firstMatchingRequest = requestsByDate.filter(req => req.status === status);
    }

    if (firstMatchingRequest) {
      //select first request
      const firstRequest =
        status === 'Closed' ? firstMatchingRequest[firstMatchingRequest.length - 1] : firstMatchingRequest[0];
      if (firstRequest) {
        // Set the selected item to the first request with the new status
        dispatch({ type: 'SET_SELECTED_ITEM', payload: firstRequest });

        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.set('requestId', firstRequest.id);
        newParams.set('status', status);
        // Use the navigate function from react-router-dom to update the URL
        navigate(`?${newParams.toString()}`, { replace: true });
      }
    } else {
      // If no request matches the new status, clear the selected item
      dispatch({ type: 'SET_SELECTED_ITEM', payload: null });

      // Update URL parameters to remove requestId and status
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('requestId');
      newParams.delete('status');

      // Use the navigate function from react-router-dom to update the URL
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  // Listen for queue updates events
  useEffect(() => {
    const handleRequestResolve = (event: CustomEvent<QueueEventPayload>) => {
      // If a request status has changed, show a notification
      if (event.detail?.message) {
        setNotification(event.detail.message);

        // Auto-hide notification after 5 seconds
        const timer = setTimeout(() => {
          setNotification(null);
        }, 5000);

        return () => clearTimeout(timer);
      }
    };

    window.addEventListener('onQueueRequestResolve', handleRequestResolve as EventListener);
    return () => {
      window.removeEventListener('onQueueRequestResolve', handleRequestResolve as EventListener);
    };
  }, []);

  // Define display labels that will be shown in the UI
  const displayLabelsCustomer: SwitchProps[] = [
    { label: QueueLabelMap.FOR_ME, value: 'Open', tooltip: QueueLabelMap.FOR_ME },
    { label: 'For KS', value: 'In Progress', tooltip: QueueLabelMap.KITCHEN_SYNC },
    { label: QueueLabelMap.CLOSED, value: 'Closed', tooltip: QueueLabelMap.CLOSED },
  ];

  const displayLabelsBookkeeper: SwitchProps[] = [
    { label: QueueLabelMap.FOR_ME, value: 'Open', tooltip: QueueLabelMap.FOR_ME },
    { label: QueueLabelMap.FOR_CLIENTS, value: 'In Progress', tooltip: QueueLabelMap.FOR_CLIENTS },
    { label: QueueLabelMap.CLOSED, value: 'Closed', tooltip: QueueLabelMap.CLOSED },
  ];

  const displayLabels = currentLocationAbility?.can(HoneAbilityActions.Read, HoneAbilitySubjects.Queue)
    ? displayLabelsBookkeeper
    : displayLabelsCustomer;

  const switchType = useMemo(() => {
    if (selectedItem?.priority === 'Normal') {
      return filters.status;
    }
    return 'Blocker';
  }, [selectedItem]);

  return (
    <div className={prefix()}>
      <div className={classNames(prefix('filters'))}>
        <div className={prefix('filters-switch')}>
          <EnhancedLabelSwitch
            labels={displayLabels}
            selected={filters.status}
            onSelect={value => handleStatusChange(value as AskRequestStatus)}
            type={switchType}
          />
        </div>
        <div>
          <button onClick={toggleCreateRequestModal} className={prefix('filters-switch-create-request-toggle')}>
            <IconAdd />
          </button>
        </div>
      </div>

      {/* Notification banner */}
      {notification && (
        <div className={prefix('notification')}>
          <div className={prefix('notification-message')}>{notification}</div>
          <button className={prefix('notification-close')} onClick={() => setNotification(null)}>
            ×
          </button>
        </div>
      )}

      <div className={classNames(prefix('request-browser'))}>
        <RequestsBrowser />
      </div>
      <QueueCreateAskReq isOpen={createRequestModalOpen} handleClose={toggleCreateRequestModal} />
    </div>
  );
};
export default BrowserColumn;
